/**
 * Supplier API 타입 정의
 */

import { getSupplierStatusLabel } from "@/shared/utils/labelConverterHelpers";
import { formatDateToDisplay } from "@/shared/utils/formatter";

// 페이지네이션 상수
export const ITEMS_PER_PAGE = {
    DEFAULT: 10,
    LARGE: 15,
    EXTRA_LARGE: 20,
} as const;

/**
 * 협력사 기본 정보
 */
export interface SupplierDto {
    suppId: string;
    suppName: string;
}

/**
 * 협력사 상세 정보 (선택 페이지용)
 */
export interface SupplierDetailDto {
    suppId: string;              // 협력사 ID (DP코드)
    suppName: string;            // 협력사명
    mngPhoneNum?: string;        // 협력사 연락처
    statusCode: string;          // 운영상태 코드
    suppGrade?: string | null;   // 협력사 등급
    gradeName?: string | null;   // 협력사 등급명
    suppType: string | null;     // 협력사 타입 (F:가맹, D:직영)
    insDate?: string;            // 등록일자
    endMngDay?: string | null;   // 운영종료일자
    endDate?: string | null;     // 운영종료일자 (legacy)
    compCeoName?: string | null; // 사업주명 (대표자명)
    suppCompName?: string | null;// 상호명
    suppNum?: string | null;     // 사업자등록번호
    acctName?: string | null;    // 예금주
    bankName?: string | null;    // 은행명
    accountNum?: string | null;  // 계좌번호
    email?: string | null;       // 이메일주소
    addrInfo?: string | null;    // 사업자소재지
    bizType?: string | null;     // 업태
    bizItem?: string | null;     // 종목
    taxType?: string | null;     // 과세자 유형
    deliveryArea?: string | null; // 배달권역
    slotSetCnt?: number | null;   // 계약 SET 수
}

/**
 * 협력사 목록 응답 DTO
 * GET /api/v1/supplier/list
 */
export interface SupplierListResponseDto {
    totalCount: number;
    suppliers: SupplierDetailDto[];
}

/**
 * 협력사 상세 목록 응답 DTO (선택 페이지용)
 * GET /api/v1/supplier/list/detail
 */
export interface SupplierDetailListResponseDto {
    totalCount: number;
    suppliers: SupplierDetailDto[];
}

/**
 * 도메인 모델 - Supplier
 */
export interface Supplier {
    id: string;
    name: string;
}

/**
 * 도메인 모델 - 협력사 상세 (선택 페이지용)
 */
export interface SupplierDetail {
    id: string;                  // 협력사 ID (DP코드)
    name: string;                // 협력사명
    phoneNumber: string;         // 협력사 연락처
    operationStatus: string;     // 운영상태 (한글)
    suppType: string;            // 협력사 타입
    grade: string;               // 등급
    registrationDate: string;    // 등록일자
    endDate: string;             // 운영종료일자
    ownerName: string;           // 사업주명 (대표자명)
    companyName: string;         // 상호명
    businessNumber: string;      // 사업자등록번호
    accountHolder: string;       // 예금주
    bankName: string;            // 은행명
    accountNumber: string;       // 계좌번호
    email: string;               // 이메일주소
    address: string;             // 사업자소재지
    bizType: string;             // 업태
    bizItem: string;             // 종목
    taxType: string;             // 과세자 유형
    ratePlan: string;            // 요금제 (추후 개발)
    standardArea: string;        // 표준권역
    slotSetCnt: string;          // 계약 SET 수
}

/**
 * 도메인 모델 - SupplierList
 */
export interface SupplierList {
    totalCount: number;
    suppliers: Supplier[];
}

/**
 * 도메인 모델 - 협력사 상세 목록
 */
export interface SupplierDetailList {
    totalCount: number;
    suppliers: SupplierDetail[];
}

/**
 * DTO → Domain 변환 함수
 */
export const supplierFromDto = (dto: SupplierDto): Supplier => ({
    id: dto.suppId,
    name: dto.suppName,
});

/** DELIVERY_AREA에서 표준권역명 추출 ("표준강원동해A (법정동 : ...)" -> "표준강원동해A") */
const extractStandardAreas = (deliveryArea: string | null | undefined): string => {
    if (!deliveryArea) return '-';
    const match = deliveryArea.match(/^([^\s(]+)/);
    return match ? match[1] : '-';
};

/** 과세자 유형 라벨 변환 */
const getTaxTypeLabel = (taxType: string | null | undefined): string => {
    if (!taxType) return '-';
    const taxTypeMap: Record<string, string> = {
        'GENERAL': '일반과세자',
        'SIMPLIFIED': '간이과세자',
        'EXEMPT': '면세사업자'
    };
    return taxTypeMap[taxType] || taxType;
};

export const supplierDetailFromDto = (dto: SupplierDetailDto): SupplierDetail => ({
    id: dto.suppId,
    name: dto.suppName,
    phoneNumber: dto.mngPhoneNum || '-',
    operationStatus: getSupplierStatusLabel(dto.statusCode),
    suppType: dto.suppType === 'F' ? '가맹' : dto.suppType === 'D' ? '직영' : (dto.suppType || '-'),
    grade: dto.gradeName || dto.suppGrade || '-',
    registrationDate: formatDateToDisplay(dto.insDate) || '-',
    endDate: formatDateToDisplay(dto.endMngDay || dto.endDate) || '-',
    ownerName: dto.compCeoName || '-',
    companyName: dto.suppCompName || '-',
    businessNumber: dto.suppNum || '-',
    accountHolder: dto.acctName || '-',
    bankName: dto.bankName || '-',
    accountNumber: dto.accountNum || '-',
    email: dto.email || '-',
    address: dto.addrInfo || '-',
    bizType: dto.bizType || '-',
    bizItem: dto.bizItem || '-',
    taxType: getTaxTypeLabel(dto.taxType),
    ratePlan: '-',  // 추후 개발
    standardArea: extractStandardAreas(dto.deliveryArea),
    slotSetCnt: dto.slotSetCnt != null ? String(dto.slotSetCnt) : '-',
});

export const supplierListFromDto = (dto: SupplierListResponseDto): SupplierList => ({
    totalCount: dto.totalCount,
    suppliers: dto.suppliers.map(supplierFromDto),
});

export const supplierDetailListFromDto = (dto: SupplierDetailListResponseDto): SupplierDetailList => ({
    totalCount: dto.totalCount,
    suppliers: dto.suppliers.map(supplierDetailFromDto),
});
